import { AccountFeatures, AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import Toggle from "react-toggle"
import IntlProps from "../../state/helpers/IntlProps"
import { PAYMENT_TYPE } from "../../state/helpers/Payment"
import { PROPOSAL_TYPE } from "../../state/proposal/ProposalType"
import Icon from "../icon/icon.component"
import LoadingSpinner from "../spinner/loading.component"
import DayInput from "./DayInput"
import RecurringPaymentMethodSelector from "./PaymentMethodSelector"
import { messages } from "./messages"

interface OwnProps {
    show: boolean
    canBeDisabled: boolean
    loading: boolean
    documentType: PROPOSAL_TYPE
    options: RecurringPaymentOptions
    onUpdate: (options: RecurringPaymentOptions) => void
    onClose: () => void
    onSave: () => void
}

interface RecurringPaymentOptions {
    enabled: boolean
    sendEmail: boolean
    attachPdf: boolean
    capturePayment: boolean
    paymentMethod: PAYMENT_TYPE
    day: number
}

const RecurringOptionsDropdown = (props: OwnProps & IntlProps) => {
    const updateOptions = (key: string, value: boolean | PAYMENT_TYPE | number) => {
        props.onUpdate({
            ...props.options,
            [key]: value
        })
    }

    const canAttachPdf = (): boolean => {
        return props.documentType === PROPOSAL_TYPE.ACCOUNT_STATEMENT
            && props.options.sendEmail
            && AppContext.accountFeatureIsEnabled(AccountFeatures.FeatureAccountStatements)
            && AppContext.accountFeatureIsEnabled(AccountFeatures.FeatureStatementAutomation)
    }

    return (
        <div className={"dropdown-menu p-4 w-100 top-inherit " + (props.show ? "show" : "")}>
            <div className="w-100 mb-1">
                {props.canBeDisabled && (
                    <div className="w-100 d-flex">
                        <div className="mr-2">
                            <Toggle
                                id="enabled"
                                defaultChecked={props.options.enabled}
                                checked={props.options.enabled}
                                icons={false}
                                disabled={props.loading}
                                onChange={() => updateOptions("enabled", !props.options.enabled)}
                            />
                        </div>
                        <label htmlFor="enabled">
                            {props.intl.formatMessage(messages.toggleEnabled)}
                        </label>
                    </div>
                )}
                {(!props.canBeDisabled || props.options.enabled) && (
                    <>
                        <div className="w-100 d-flex">
                            <div className="mr-2">
                                <Toggle
                                    id="sendEmail"
                                    defaultChecked={props.options.sendEmail}
                                    checked={props.options.sendEmail}
                                    icons={false}
                                    disabled={props.loading}
                                    onChange={() => updateOptions("sendEmail", !props.options.sendEmail)}
                                />
                            </div>
                            <label htmlFor="sendEmail">
                                {props.intl.formatMessage(messages.toggleSendEmail)}
                            </label>
                        </div>

                        {canAttachPdf() && (
                            <div className="w-100 d-flex">
                                <div className="ml-5">
                                    <Toggle
                                        id="attachPdf"
                                        defaultChecked={props.options.attachPdf}
                                        checked={props.options.attachPdf}
                                        icons={false}
                                        disabled={props.loading}
                                        onChange={() => updateOptions("attachPdf", !props.options.attachPdf)}
                                    />
                                </div>
                                <label className="ml-2" htmlFor="attachPdf">
                                    <FormattedMessage
                                        id="contacts.statement.recurring.options.toggle_attach_pdf"
                                        defaultMessage="Attach account statement pdf"
                                    />
                                </label>
                            </div>
                        )}
            
                        <RecurringPaymentMethodSelector
                            toggled={props.options.capturePayment}
                            selectedPaymentMethod={props.options.paymentMethod}
                            loading={props.loading}
                            onToggle={() => updateOptions("capturePayment", !props.options.capturePayment)}
                            onSelectPaymentMethod={(method: PAYMENT_TYPE) => updateOptions("paymentMethod", method)}
                        />

                        <DayInput
                            documentType={props.documentType}
                            sendEmail={props.options.sendEmail}
                            day={props.options.day}
                            loading={props.loading}
                            onUpdate={(day: number) => updateOptions("day", day)}
                        />
                    </>
                )}
            </div>
            <p className="m-0 pt-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={props.onClose} disabled={props.loading}>
                    <Icon value="arrow-left" />
                </button>
                <button className="btn btn-sm btn-outline-primary ml-2" onClick={props.onSave} disabled={props.loading}>
                    {props.loading ? <LoadingSpinner height={15} /> : <Icon value="save" />}
                </button>
            </p>
        </div>
    )
}

export default injectIntl(RecurringOptionsDropdown)


// WEBPACK FOOTER //
// ./src/common/recurring-options-dropdown/RecurringOptionsDropdown.tsx